import React, { useMemo, useState, useEffect } from 'react'
import { GetCumulativeReport, useCustomReportType } from 'hooks/report'
import { GetCumulativeReportCustomerList } from 'hooks/cumulativeReportCustomer'
import { GetStatusList } from 'hooks'
import { Form, Tab } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import {
    FormDateField,
    FormDropdownField,
    SubmitCancelButtons,
} from 'elements'
import { LoaderBar, PageHeader } from 'components'
import { DatePickerTypes, DateUtility } from 'utility'
import styled from 'styled-components'
import { BaseService } from 'utility/services/base'
import { APIPath } from 'utility/constant'

const StyledCard = styled.div`
    .ui.table {
        border: none;
    }
    .total-row {
        background-color: #f9fafb;
        font-weight: bold;
    }
    .grand-total-row {
        background-color: #f5f5f5;
        font-weight: bold;
    }
    .clickable-child {
        cursor: pointer;
        color: #0066cc;
        &:hover {
            text-decoration: underline;
        }
    }
`

const StyledForm = styled(Form)`
    .date-fields {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    .dropdown-fields {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    @media (max-width: 767px) {
        .dropdown-fields {
            flex-direction: column;
        }

        .dropdown-fields > * {
            margin-bottom: 1rem;
        }
    }
`

const CumulativeReportSchema = yup.object().shape({
    StartDate: yup
        .string()
        .required('Start date required')
        .typeError('Start date required'),
    EndDate: yup
        .string()
        .required('End date required')
        .typeError('End date required'),
    CompanyId: yup
        .array()
        .nullable(),
    StatusId: yup
        .string()
        .nullable(),
    TeamMemberId: yup
        .array()
        .nullable(),
})

const getMondayOfCurrentWeek = () => {
    const today = new Date()
    const day = today.getDay()
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
    return new Date(today.setDate(diff))
}

const getToday = () => {
    return new Date()
}

export const CumulativeReport = () => {
    const navigate = useNavigate()
    const { data, loading, filterChanged } = GetCumulativeReport()
    const { options: customerOptions, data: customerData } = GetCumulativeReportCustomerList()
    const { options: statusOptions } = GetStatusList()
    const [teamMemberOptions, setTeamMemberOptions] = useState([])
    const { reportName } = useCustomReportType(3) // 3 is the ReportId for Cumulative Report

    const fetchTeamMembers = async () => {
        try {
            const response = await BaseService.get(APIPath.teamMember)
            const options = response.map(member => ({
                key: member.unique_id,
                text: member.Name || member.Email,
                value: member.unique_id.toString(),
            }))
            setTeamMemberOptions(options)
        } catch (error) {
            console.error('Error fetching team members:', error)
        }
    }

    useEffect(() => {
        fetchTeamMembers()
    }, [])

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CumulativeReportSchema),
        defaultValues: {
            StartDate: getMondayOfCurrentWeek(),
            EndDate: getToday(),
            CompanyId: [],
            StatusId: '',
            TeamMemberId: [],
        },
    })

    const StartDate = watch('StartDate')

    // Process data to include totals
    const processedData = useMemo(() => {
        if (!data || data.length === 0) return []

        const result = []
        let currentParent = null
        let parentTotal = { Score: 0, MaxPossibleScore: 0 }
        const grandTotal = { Score: 0, MaxPossibleScore: 0 }

        data.forEach((item, index) => {
            // If parent changes, add the previous parent's total
            if (currentParent && currentParent !== item.ParentName && parentTotal.MaxPossibleScore > 0) {
                result.push({
                    ParentName: `${currentParent} Total`,
                    ChildName: '',
                    Score: parentTotal.Score,
                    MaxPossibleScore: parentTotal.MaxPossibleScore,
                    isTotal: true,
                })
                // Reset parent total for new parent
                parentTotal = { Score: 0, MaxPossibleScore: 0 }
            }

            // Add current row
            result.push(item)

            // Update totals
            parentTotal.Score += item.Score
            parentTotal.MaxPossibleScore += item.MaxPossibleScore
            grandTotal.Score += item.Score
            grandTotal.MaxPossibleScore += item.MaxPossibleScore

            // Update current parent
            currentParent = item.ParentName

            // If last item, add the final parent total
            if (index === data.length - 1 && parentTotal.MaxPossibleScore > 0) {
                result.push({
                    ParentName: `${currentParent} Total`,
                    ChildName: '',
                    Score: parentTotal.Score,
                    MaxPossibleScore: parentTotal.MaxPossibleScore,
                    isTotal: true,
                })
            }
        })

        // Add grand total
        if (grandTotal.MaxPossibleScore > 0) {
            result.push({
                ParentName: 'Grand Total',
                ChildName: '',
                Score: grandTotal.Score,
                MaxPossibleScore: grandTotal.MaxPossibleScore,
                isGrandTotal: true,
            })
        }

        return result
    }, [data])

    const submit = formData => {
        // Convert selected customer IDs and collect unique_ids
        const selectedCompanyIds = []
        const selectedCustomerIds = []

        formData.CompanyId.forEach(uniqueId => {
            const customer = customerData.find(c => c.unique_id === parseInt(uniqueId, 10))
            if (customer) {
                selectedCompanyIds.push(customer.company_id)
                selectedCustomerIds.push(customer.unique_id)
            } else {
                const parsedId = parseInt(uniqueId, 10)
                selectedCompanyIds.push(parsedId)
                selectedCustomerIds.push(parsedId)
            }
        })

        // Get selected team member unique_ids directly from the form data
        const selectedTeamMemberIds = formData.TeamMemberId?.map(id => parseInt(id, 10)) || []

        filterChanged({
            StartDate: DateUtility.dateToLocalISO(
                DateUtility.startDay(new Date(formData.StartDate)),
            ),
            EndDate: DateUtility.dateToLocalISO(
                DateUtility.endDay(new Date(formData.EndDate)),
            ),
            CompanyId: selectedCompanyIds.join(';'),
            StatusList: formData.StatusId ? parseInt(formData.StatusId, 10) : null,
            CustomerIds: selectedCustomerIds.join(';'), // Customer unique_ids from customer API
            UserIds: selectedTeamMemberIds.join(';'), // Team member unique_ids from team member API
        })
    }

    const resetForm = () => {
        reset({
            StartDate: '',
            EndDate: '',
            CompanyId: [],
            StatusId: '',
            TeamMemberId: [],
        })
    }

    const getRowClassName = (item) => {
        if (item.isGrandTotal) return 'grand-total-row'
        if (item.isTotal) return 'total-row'
        return ''
    }

    const handleChildNameClick = (item) => {
        if (item.ReportLinks && item.ReportLinks.length === 0) {
            // Navigate to read-only page with template ID
            navigate(`/app/templates/inspection/${item.TemplateId}`)
        }
    }

    const panes = [
        {
            menuItem: 'Data',
            render: () => (
                <StyledCard>
                    <table className="ui celled table">
                        <thead>
                            <tr>
                                <th>Site</th>
                                <th>Zone</th>
                                <th>Score</th>
                                <th>Max</th>
                                <th>HK%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {processedData.length > 0 ? (
                                processedData.map((item, index) => (
                                    <tr key={index} className={getRowClassName(item)}>
                                        <td>{item.ParentName}</td>
                                        <td>
                                            {item.ChildName && !item.isTotal && !item.isGrandTotal && (
                                                <div
                                                    className={item.ReportLinks?.length === 0 ? "clickable-child" : ""}
                                                    onClick={() => item.ReportLinks?.length === 0 && handleChildNameClick(item)}
                                                >
                                                    {item.ChildName}
                                                </div>
                                            )}
                                            {item.ReportLinks?.length > 0 && (
                                                <div className="mt-1">
                                                    {item.ReportLinks.map((report, idx) => (
                                                        <a
                                                            key={idx}
                                                            href={report.ReportPath}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'block',
                                                                fontSize: '0.9em',
                                                                color: '#0066cc',
                                                                textDecoration: 'underline',
                                                                marginTop: '2px',
                                                            }}
                                                        >
                                                            {report.AuditName}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.Score}</td>
                                        <td>{item.MaxPossibleScore}</td>
                                        <td>
                                            {((item.Score / item.MaxPossibleScore) * 100).toFixed(2)}%
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center' }}>
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </StyledCard>
            ),
        },
    ]

    return (
        <>
            <div>
                <PageHeader header={reportName || 'Cumulative Report'} />
                <StyledForm noValidate>
                    <div className="date-fields">
                        <FormDateField
                            name="StartDate"
                            control={control}
                            label="Start Date"
                            errors={errors.StartDate}
                            type={DatePickerTypes.Single}
                        />
                        <FormDateField
                            name="EndDate"
                            control={control}
                            label="End Date"
                            disabledBefore={StartDate}
                            errors={errors.EndDate}
                            type={DatePickerTypes.Single}
                        />
                    </div>
                    <div className="dropdown-fields">
                        <FormDropdownField
                            name="CompanyId"
                            control={control}
                            label="Customer"
                            errors={errors.CompanyId}
                            options={customerOptions}
                            placeholder="Select Customers"
                            multiple
                            search
                            defaultValue={[]}
                        />
                        <FormDropdownField
                            name="StatusId"
                            control={control}
                            label="Status"
                            errors={errors.StatusId}
                            options={statusOptions}
                            placeholder="Select Status"
                            search
                            defaultValue=""
                        />
                        <FormDropdownField
                            name="TeamMemberId"
                            control={control}
                            label="Team Member"
                            errors={errors.TeamMemberId}
                            options={teamMemberOptions}
                            placeholder="Select Team Members"
                            multiple
                            search
                            defaultValue={[]}
                        />
                    </div>
                    <div className="pt-1">
                        <SubmitCancelButtons
                            onSubmit={handleSubmit(submit)}
                            onCancel={resetForm}
                        />
                    </div>
                </StyledForm>
            </div>
            <div className="my-3">
                <Tab panes={panes} menu={{ secondary: true, pointing: true }} />
                {loading && <LoaderBar content="Loading..." />}
            </div>
        </>
    )
}